// src/utils/htmlNoteSegments.js

/**
 * Segmentação de notas sobre a projeção de um conteúdo HTML.
 *
 * O `TextWithNotes.vue` (texto puro) fatia `content` linearmente e renderiza um
 * `v-for` plano. Em HTML isso não serve: o conteúdo é uma árvore, e um trecho de
 * nota pode atravessar `<p>`, `<em>` e células de tabela. Este módulo separa as
 * duas metades do problema:
 *
 *  1. **onde as notas começam e terminam** — puro, sobre a projeção em texto puro,
 *     que é exatamente a régua dos offsets gravados. É o que está aqui.
 *  2. **como pintar isso na árvore** — trabalho do componente, que percorre o DOM
 *     e recorta cada text node nas fronteiras calculadas aqui.
 *
 * Manter (1) fora do componente deixa a parte que erra em silêncio — a aritmética
 * de offset — testável sem montar um componente Vue.
 */

// Extensão explícita: este módulo também é carregado fora do webpack.
import { compareNotesByTextOrder } from "./noteOrdering.js";

/**
 * Um trecho de texto sem quebras internas de nota, sempre acompanhado de um span
 * do texto puro (`<span>`, `<b>`…). O componente injeta um espaço a cada fatia.
 */
const WHITESPACE = /\s/;

/**
 * `true` se o trecho é uma palavra só — o que muda a estilização: palavra única
 * ganha cor e negrito, trecho de várias palavras ganha fundo.
 */
export function isSingleWord(text) {
  return typeof text === "string" && text.trim().length > 0 && !/\s/.test(text.trim());
}

/**
 * Calcula onde a régua muda de estado.
 *
 * @param {string} text Projeção em texto puro. **É a régua dos offsets das notas.**
 * @param {Array} notes Notas com `textRangeStart` / `textRangeEnd`.
 * @returns {{
 *   segments: Array<{start: number, end: number, notes: Array}>,
 *   exponents: Array<{pos: number, exponent: number, notes: Array, isContinuation: boolean}>
 * }}
 *   `segments` cobre `[0, text.length)` de forma contígua; cada um carrega as notas
 *   ativas naquele intervalo (vazio onde não há nota). `exponents` são marcas de
 *   comprimento zero, na posição de término de cada nota.
 */
export function computeNoteSegments(text, notes) {
  const length = typeof text === "string" ? text.length : 0;
  if (length === 0) return { segments: [], exponents: [] };

  const valid = (Array.isArray(notes) ? notes : []).filter((note) => {
    const start = note?.textRangeStart;
    const end = note?.textRangeEnd;
    if (typeof start !== "number" || typeof end !== "number") return false;
    // 0–0 é a convenção do projeto para nota sem trecho ancorado.
    if (start === 0 && end === 0) return false;
    if (start < 0 || end < 0 || start >= end) return false;
    if (start >= length) return false;
    return true;
  });

  const prepared = valid.map((note) => ({
    ...note,
    isOriginalSingleWord: isSingleWord(text.slice(note.textRangeStart, note.textRangeEnd)),
  }));

  const events = [];
  const endEvents = [];

  for (const note of prepared) {
    events.push({ pos: note.textRangeStart, note, action: "start" });
    const endEvent = { pos: note.textRangeEnd, note, action: "end" };
    events.push(endEvent);
    endEvents.push(endEvent);
  }

  // A numeração dos expoentes segue o INÍCIO do trecho, não a ordem de criação
  // nem a de término: entre duas notas, recebe o número menor a que começa antes.
  // Num trecho aninhado (a = 1–100 contendo b = 50–70), `a` é 1 e `b` é 2.
  //
  // ⚠️ A MARCA continua sendo emitida na posição de término (ver `exponents`
  // abaixo), então nesse caso o `2` aparece na tela antes do `1`. Numerar pelo
  // começo e marcar no fim são decisões independentes.
  //
  // O comparador é compartilhado — ver `noteOrdering.js`.
  endEvents.sort((a, b) => compareNotesByTextOrder(a.note, b.note));
  const exponentByNote = new Map();
  endEvents.forEach((event, index) => exponentByNote.set(event.note, index + 1));

  // Em empate de posição, "end" fecha antes de "start" abrir. A comparação olha as
  // duas ações de propósito: devolver -1 só porque `a` é "end", sem consultar `b`,
  // inverte a ordem de duas notas que terminam no mesmo offset.
  //
  // A terceira chave só decide entre dois "end" no MESMO offset, e existe para
  // as marcas coladas ("1, 2") saírem em ordem crescente: com a numeração pelo
  // início, duas notas que terminam juntas podem ter números fora de sequência.
  // Reordenar dois "end" é seguro — remover de um Set não mexe na ordem do que
  // fica. Fazer o mesmo com dois "start" mudaria a ordem de `active`, e com ela
  // qual nota fica por cima no realce.
  const rank = (action) => (action === "end" ? 0 : 1);
  events.sort(
    (a, b) =>
      a.pos - b.pos ||
      rank(a.action) - rank(b.action) ||
      (a.action === "end" ? compareNotesByTextOrder(a.note, b.note) : 0)
  );

  const segments = [];
  const exponents = [];
  const active = new Set();
  let lastPos = 0;

  for (const event of events) {
    if (event.pos > lastPos) {
      segments.push({ start: lastPos, end: event.pos, notes: Array.from(active) });
    }

    if (event.action === "end") {
      exponents.push({
        pos: event.pos,
        exponent: exponentByNote.get(event.note),
        notes: [event.note],
        isContinuation: false,
      });
      active.delete(event.note);
    } else {
      active.add(event.note);
    }

    lastPos = event.pos;
  }

  if (lastPos < length) {
    segments.push({ start: lastPos, end: length, notes: Array.from(active) });
  }

  // Expoentes na mesma posição saem como "1, 2, 3": a vírgula entra a partir do
  // segundo. Como as marcas têm comprimento zero, "mesma posição" é o teste.
  for (let i = 1; i < exponents.length; i += 1) {
    exponents[i].isContinuation = exponents[i].pos === exponents[i - 1].pos;
  }

  return { segments, exponents };
}

/**
 * Recorta o intervalo de um text node nas fronteiras de nota que caem dentro dele.
 *
 * @param {number} nodeStart Offset do início do nó na projeção.
 * @param {number} nodeEnd Offset do fim, exclusive.
 * @param {Array} segments Saída de {@link computeNoteSegments}.
 * @returns {Array<{start: number, end: number, notes: Array}>}
 *   Fatias cobrindo `[nodeStart, nodeEnd)`. Um nó inteiramente fora de qualquer
 *   nota devolve uma fatia só, sem notas.
 */
export function sliceNodeBySegments(nodeStart, nodeEnd, segments) {
  const pieces = [];

  for (const segment of segments) {
    if (segment.end <= nodeStart) continue;
    if (segment.start >= nodeEnd) break;

    pieces.push({
      start: Math.max(segment.start, nodeStart),
      end: Math.min(segment.end, nodeEnd),
      notes: segment.notes,
    });
  }

  // Sem nota nenhuma no documento, `segments` cobre tudo com notes vazio; mas se o
  // nó cair fora da cobertura (conteúdo sem notas), devolve o nó inteiro.
  if (pieces.length === 0 && nodeEnd > nodeStart) {
    pieces.push({ start: nodeStart, end: nodeEnd, notes: [] });
  }

  return pieces;
}

/**
 * Quebra fatias muito longas, para não gerar um único nó de texto gigante.
 * Espelha o `MAX_SEGMENT_TEXT_LENGTH` do renderizador de texto puro, mas aplicado
 * por text node.
 */
export function chunkPieces(pieces, maxLength) {
  if (!maxLength || maxLength <= 0) return pieces;

  const out = [];
  for (const piece of pieces) {
    if (piece.end - piece.start <= maxLength) {
      out.push(piece);
      continue;
    }
    for (let at = piece.start; at < piece.end; at += maxLength) {
      out.push({
        start: at,
        end: Math.min(at + maxLength, piece.end),
        notes: piece.notes,
      });
    }
  }
  return out;
}

export { WHITESPACE };
